import { AWS_REGIONS } from '../../shared/constants/aws';
import { safeFormatRelative } from '../../shared/utils/dateUtils';
import { BaselineLayer, type AccountBaselineView } from '../../types/backend';
import { BaselineHealth, summarizeBaselines, type BaselineSummary } from './baselineStatus';
import { REACHABILITY_PROBE } from './deriveOnboardingGates';

/** "Frankfurt" out of "eu-central-1 (Europe - Frankfurt)", or the code when the region is not listed. */
export function regionCity(code: string): string {
  const label = AWS_REGIONS.find((r) => r.value === code)?.label;
  return label?.match(/ - ([^)]+)\)$/)?.[1] ?? code;
}

/** What the recorded reachability probe says, or that none has run. */
export function reachabilityText(account: AccountBaselineView | undefined): string {
  const probe = account?.probes?.find((p) => p.name === REACHABILITY_PROBE);
  if (!probe) return 'Not checked yet';
  const when = probe.checked_at ? `, checked ${safeFormatRelative(probe.checked_at, 'at an unreadable time').toLowerCase()}` : '';
  if (probe.inconclusive) return `Could not check: ${probe.summary || 'the check did not complete'}${when}`;
  if (!probe.ok) return `Not reachable: ${probe.summary || 'no reason recorded'}${when}`;
  return `Reachable${when}`;
}

export function isReachable(account: AccountBaselineView | undefined): boolean {
  const probe = account?.probes?.find((p) => p.name === REACHABILITY_PROBE);
  return Boolean(probe?.ok && !probe.inconclusive);
}

/** How the account's external ID relates to the customer's default. */
export function externalIdNote(accountId: string | undefined, customerDefault: string | undefined): string {
  if (customerDefault && accountId === customerDefault) return '(default)';
  return customerDefault ? `(set for this account; default ${customerDefault})` : '(set for this account)';
}

/** Onboarded means every foundation baseline is in sync. */
export function isOnboarded(baselines: BaselineSummary[]): boolean {
  const foundation = baselines.filter((b) => b.layer === BaselineLayer.FOUNDATION);
  return foundation.length > 0 && foundation.every((b) => b.health === BaselineHealth.IN_SYNC);
}

/** Every baseline the account carries that is not in sync. */
export function needsAttention(baselines: BaselineSummary[]): BaselineSummary[] {
  return baselines.filter((b) => b.health !== BaselineHealth.IN_SYNC && b.health !== BaselineHealth.OFF);
}

export type StepState = 'done' | 'current' | 'problem' | 'todo';

export interface ChecklistStep {
  title: string;
  state: StepState;
  status: string;
  body?: string;
}

const HALTED: string[] = [BaselineHealth.REFUSED, BaselineHealth.BLOCKED];

/** The five steps from a registered account to an onboarded one, from what the status read records. */
export function onboardingChecklist(
  account: AccountBaselineView,
  names: { accountName: string; mainRegion: string; otherRegions: number },
): ChecklistStep[] {
  const reachable = isReachable(account);
  const started = Boolean(account.baseline_managed);
  const foundation = summarizeBaselines(account).filter((b) => b.layer === BaselineLayer.FOUNDATION);
  const halted = foundation.find((b) => HALTED.includes(b.health));
  const inSync = foundation.filter((b) => b.health === BaselineHealth.IN_SYNC).length;
  const reachProbe = account.probes?.find((p) => p.name === REACHABILITY_PROBE);

  return [
    { title: 'Account registered', state: 'done', status: `${names.accountName}, ${account.aws_account_id}` },
    {
      title: 'Customer installs the onboarding role',
      state: reachable ? 'done' : reachProbe && !reachProbe.ok && !reachProbe.inconclusive ? 'problem' : 'current',
      status: reachabilityText(account),
      body: reachable ? undefined
        : 'The customer installs one role, cloud2-onboarding, that trusts Cloud2’s operations account with this '
          + 'account’s external ID. OpsPilot checks it on its own and shows the result here.',
    },
    {
      title: 'Regions',
      state: started ? 'done' : reachable ? 'current' : 'todo',
      status: `${regionCity(names.mainRegion)} main, ${names.otherRegions} more`,
      body: started || !reachable ? undefined
        : 'The main region holds the access roles, the global baseline and the Data baseline. Every supported region, '
          + 'and us-east-1 always, gets the regional baseline. Change them on the Regions tab.',
    },
    {
      title: 'Install the Cloud2 baseline',
      state: started ? 'done' : reachable ? 'current' : 'todo',
      status: started ? 'Started' : reachable ? 'Ready to start' : '',
      body: started || !reachable ? undefined
        : 'An admin turns on “OpsPilot installs the baselines” below. OpsPilot installs the access roles first, '
          + 'then the regional baselines, the global baseline and the Data baseline, then any optional baseline.',
    },
    {
      title: 'Installing',
      state: !started ? 'todo' : halted ? 'problem' : inSync === foundation.length && inSync > 0 ? 'done' : 'current',
      status: !started ? '' : halted ? `${halted.title}: ${halted.reason ?? 'needs a person'}` : `${inSync} of ${foundation.length} baselines in sync`,
    },
  ];
}

export interface RegionDraft {
  code: string;
  isMain: boolean;
  /** On the saved account and taken off in this draft. */
  removed: boolean;
  /** Not on the saved account. */
  added: boolean;
}

/** The saved regions first, in their order, then the ones this draft adds. */
export function regionRows(saved: string[], draft: string[], main: string): RegionDraft[] {
  const codes = [...saved, ...draft.filter((c) => !saved.includes(c))];
  return codes.map((code) => ({
    code,
    isMain: code === main,
    removed: saved.includes(code) && !draft.includes(code),
    added: !saved.includes(code),
  }));
}

/** "Saving installs …; removes …; moves …." or null when the draft matches the saved account. */
export function regionChangeText(
  saved: { regions: string[]; main: string },
  draft: { regions: string[]; main: string },
): string | null {
  const added = draft.regions.filter((c) => !saved.regions.includes(c));
  const removed = saved.regions.filter((c) => !draft.regions.includes(c));
  const parts: string[] = [];
  if (added.length) parts.push(`installs the regional baseline in ${added.map(regionCity).join(', ')}`);
  if (removed.length) {
    parts.push(`removes ${added.length ? 'it' : 'the regional baseline'} from ${removed.map(regionCity).join(', ')}`);
  }
  if (draft.main !== saved.main) parts.push(`moves the main region to ${regionCity(draft.main)}`);
  return parts.length ? `Saving ${parts.join('; ')}.` : null;
}
